import { useState, useRef, useCallback, useEffect } from 'react';
import {
  transcribeAndUpdateSummary,
  VoiceNoteUploadError,
  type VoiceSmartUpdateResponse,
} from '@/lib/api/voice-note';
import { createComponentLogger } from '@/lib/utils/logger';

const logger = createComponentLogger('useVoiceNote');

export type VoiceNoteState = 'idle' | 'recording' | 'processing';

interface UseVoiceNoteOptions {
  /** Build the current summary snapshot for smart update */
  getCurrentSummary: () => Record<string, unknown>;
  /** Called when the summary is updated with voice note data */
  onSummaryUpdated: (response: VoiceSmartUpdateResponse) => void;
  /** Called on error */
  onError?: (error: Error, details?: { status?: number; url?: string }) => void;
}

export function useVoiceNote({
  getCurrentSummary,
  onSummaryUpdated,
  onError,
}: UseVoiceNoteOptions) {
  const [state, setState] = useState<VoiceNoteState>('idle');
  const [recordingDuration, setRecordingDuration] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
const recorder = mediaRecorderRef.current;
if (recorder && recorder.state !== 'inactive') {
  try {
    recorder.stop();
  } catch {
    // ignore
  }
}

if (streamRef.current) {
  streamRef.current.getTracks().forEach((track) => track.stop());
  streamRef.current = null;
}
mediaRecorderRef.current = null;
    setRecordingDuration(0);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Prefer webm (widely supported), fallback to mp4
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start(250); // collect in 250ms chunks
      setState('recording');

      // Duration timer
      setRecordingDuration(0);
      timerRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);
    } catch (err) {
      logger.error('Failed to start recording:', err);
      cleanup();
      const error = err instanceof Error ? err : new Error('Microphone access denied');
      onError?.(error);
    }
  }, [cleanup, onError]);

  const stopRecording = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;

    setState('processing');

    // Wait for the recorder to finish collecting data
    const audioBlob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        resolve(blob);
      };
      recorder.stop();
    });

    // Release mic stream and timer immediately
    cleanup();

    try {
      const summary = getCurrentSummary();
      const response = await transcribeAndUpdateSummary(audioBlob, summary);
      onSummaryUpdated(response);
    } catch (err) {
      if (err instanceof VoiceNoteUploadError) {
        logger.error('Voice note processing failed:', {
          message: err.message,
          status: err.status,
          url: err.url,
          details: err.details,
        });
        onError?.(err, { status: err.status, url: err.url });
      } else {
        logger.error('Voice note processing failed:', err);
        const error = err instanceof Error ? err : new Error('Voice note processing failed');
        onError?.(error);
      }
    } finally {
      setState('idle');
    }
  }, [getCurrentSummary, onSummaryUpdated, onError, cleanup]);

  const cancelRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
    cleanup();
    setState('idle');
  }, [cleanup]);

  // Ensure mic stream and timer are released if the component unmounts mid-recording
  useEffect(() => cleanup, [cleanup]);

  return {
    state,
    recordingDuration,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
